import { PublicClientApplication } from '@azure/msal-browser';

// MSAL configuration for Outlook integration
export const msalConfig = {
  auth: {
    clientId: process.env.REACT_APP_OUTLOOK_CLIENT_ID || (() => { console.error('[Outlook] REACT_APP_OUTLOOK_CLIENT_ID is not set — Outlook OAuth will fail'); return undefined; })(),
    redirectUri: window.location.origin,
    authority: "https://login.microsoftonline.com/common"
  },
  cache: {
    cacheLocation: "localStorage",
    storeAuthStateInCookie: false,
  }
};

// Create MSAL instance
export const msalInstance = new PublicClientApplication(msalConfig);

// Scopes needed for calendar access and Teams meetings
export const outlookScopes = [
  "User.Read",
  "Calendars.Read",
  "Calendars.ReadWrite",
  "OnlineMeetings.ReadWrite"
];

// Convert Outlook event to Aidey task format
export function outlookEventToTask(event) {
  const startDateTime = new Date(event.start.dateTime);
  const endDateTime = new Date(event.end.dateTime);
  
  return {
    id: `outlook_${event.id}`,
    title: event.subject || "Untitled event",
    date: startDateTime.toISOString().slice(0, 10),
    time: event.isAllDay ? null : startDateTime.toTimeString().slice(0, 5),
    deadline: endDateTime.toISOString(),
    details: event.bodyPreview || "",
    priority: event.importance === 'high' ? 'high' : 'medium',
    external: true,
    editable: false,
    completed: false,
    category: "outlook",
    source: "Outlook",
    originalEvent: event,
    isAllDay: event.isAllDay
  };
}

// Fetch events from Microsoft Graph API
export async function fetchOutlookEvents(accessToken) {
  try {
    // ✅ Validate accessToken before fetch requests
    if (!accessToken || typeof accessToken !== 'string' || accessToken.trim().length === 0) {
      throw new Error('Valid access token is required for Outlook API requests');
    }

    // Get events for the next 30 days
    const startTime = new Date().toISOString();
    const endTime = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString();
    
    const response = await fetch(
      `https://graph.microsoft.com/v1.0/me/events?$filter=start/dateTime ge '${startTime}' and start/dateTime le '${endTime}'&$orderby=start/dateTime&$top=100`,
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to fetch Outlook events: ${response.status} ${response.statusText}`);
    }

    // ✅ Add try/catch around Graph API JSON parsing
    let data;
    try {
      data = await response.json();
    } catch (parseError) {
      console.error('Error parsing Outlook API response:', parseError);
      throw new Error('Invalid JSON response from Microsoft Graph API');
    }
    
    if (!data.value || !Array.isArray(data.value)) {
      throw new Error('Invalid response format from Microsoft Graph API');
    }

    return data.value.map(outlookEventToTask);
  } catch (error) {
    console.error('Error fetching Outlook events:', error);
    throw error;
  }
}

// Create event in Outlook calendar
export async function createOutlookEvent(accessToken, task) {
  try {
    // ✅ Validate accessToken before fetch requests
    if (!accessToken || typeof accessToken !== 'string' || accessToken.trim().length === 0) {
      throw new Error('Valid access token is required for Outlook API requests');
    }

    // ✅ Validate task object and required fields
    if (!task || typeof task !== 'object') {
      throw new Error('Valid task object is required');
    }
    if (!task.title || typeof task.title !== 'string') {
      throw new Error('Task title is required and must be a string');
    }
    if (!task.date || typeof task.date !== 'string') {
      throw new Error('Task date is required and must be a string');
    }

    const startTime = task.time 
      ? `${task.date}T${task.time}:00`
      : `${task.date}T09:00:00`;
    
    // Default to 1 hour duration
    const endTime = task.time 
      ? `${task.date}T${(parseInt(task.time.slice(0, 2)) + 1).toString().padStart(2, '0')}:${task.time.slice(3)}:00`
      : `${task.date}T10:00:00`;

    // ✅ Ensure subject/title and startTime are always valid when creating events
    const event = {
      subject: task.title || 'Untitled Event',
      body: {
        contentType: "text",
        content: task.details || ""
      },
      start: {
        dateTime: startTime,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      },
      end: {
        dateTime: endTime,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
      },
      importance: task.priority === 'high' ? 'high' : 'normal'
    };

    // ✅ Validate date format before creating event
    const startDate = new Date(startTime);
    const endDate = new Date(endTime);
    if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
      throw new Error('Invalid date/time format provided for event creation');
    }

    const response = await fetch(
      'https://graph.microsoft.com/v1.0/me/events',
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(event)
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to create Outlook event: ${response.status} ${response.statusText}`);
    }

    // ✅ Add try/catch around Graph API JSON parsing
    let result;
    try {
      result = await response.json();
    } catch (parseError) {
      console.error('Error parsing Outlook event creation response:', parseError);
      throw new Error('Invalid JSON response from Microsoft Graph API during event creation');
    }

    return result;
  } catch (error) {
    console.error('Error creating Outlook event:', error);
    throw error;
  }
}

// Test Outlook connection
export async function testOutlookConnection(accessToken) {
  try {
    // ✅ Validate accessToken before fetch requests
    if (!accessToken || typeof accessToken !== 'string' || accessToken.trim().length === 0) {
      return {
        success: false,
        message: 'Valid access token is required for Outlook connection test'
      };
    }

    const response = await fetch(
      'https://graph.microsoft.com/v1.0/me/calendars',
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Connection test failed: ${response.status} ${response.statusText}`);
    }

    // ✅ Add try/catch around Graph API JSON parsing
    let data;
    try {
      data = await response.json();
    } catch (parseError) {
      console.error('Error parsing Outlook connection test response:', parseError);
      return {
        success: false,
        message: 'Invalid JSON response from Microsoft Graph API during connection test'
      };
    }

    return {
      success: true,
      calendars: data.value?.length || 0,
      message: `Connected successfully! Found ${data.value?.length || 0} calendars.`
    };
  } catch (error) {
    return {
      success: false,
      message: error.message || 'Unknown error during Outlook connection test'
    };
  }
}

// Get user profile info
export async function getOutlookUserInfo(accessToken) {
  try {
    // ✅ Validate accessToken before fetch requests
    if (!accessToken || typeof accessToken !== 'string' || accessToken.trim().length === 0) {
      throw new Error('Valid access token is required for Outlook API requests');
    }

    const response = await fetch(
      'https://graph.microsoft.com/v1.0/me',
      {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      }
    );

    if (!response.ok) {
      throw new Error(`Failed to get user info: ${response.status} ${response.statusText}`);
    }

    // ✅ Add try/catch around Graph API JSON parsing
    let userInfo;
    try {
      userInfo = await response.json();
    } catch (parseError) {
      console.error('Error parsing Outlook user info response:', parseError);
      throw new Error('Invalid JSON response from Microsoft Graph API during user info retrieval');
    }

    return userInfo;
  } catch (error) {
    console.error('Error getting Outlook user info:', error);
    throw error;
  }
}
